import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Users, Loader2, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

/**
 * ReassignApporteur - Permet aux VP de réattribuer un prospect à un autre apporteur
 * 
 * Règles:
 * 1. Seuls les VP associés au dossier peuvent réattribuer
 * 2. Confirmation obligatoire
 * 3. Blocage si le VP a déjà été facturé pour ce prospect
 */
export function ReassignApporteur({ prospect, onReassigned }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [teamApporteurs, setTeamApporteurs] = useState([]);
    const [selectedApporteurId, setSelectedApporteurId] = useState('');
    const [confirmationData, setConfirmationData] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const isVP = user?.role === 'vp';
    const isVPOfDossier = prospect?.dossier_vp_id === user?.id || prospect?.dossier_vp_2_id === user?.id;

    // Ne pas afficher si pas VP du dossier
    if (!isVP || !isVPOfDossier) {
        return null;
    }

    // Charger les apporteurs de l'équipe de l'apporteur actuel (via l'endpoint dédié)
    const loadTeamApporteurs = async () => {
        setLoading(true);
        try {
            // Utiliser l'endpoint dédié qui filtre par équipe de l'apporteur actuel
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/available-apporteurs`, 
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}` }
                }
            );
            
            if (!res.ok) {
                throw new Error('Erreur lors du chargement');
            }
            
            const data = await res.json();
            setTeamApporteurs(data.apporteurs || []);
            
            // Si aucun apporteur disponible, afficher un message
            if ((data.apporteurs || []).length === 0 && data.message) {
                toast.info(data.message);
            }
        } catch (err) {
            console.error('Erreur chargement équipe:', err);
            toast.error('Erreur lors du chargement des apporteurs');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenChange = (isOpen) => {
        setOpen(isOpen);
        if (isOpen) {
            loadTeamApporteurs();
            setSelectedApporteurId('');
            setConfirmationData(null);
        }
    };

    // Première étape: demander confirmation
    const handleRequestConfirmation = async () => {
        if (!selectedApporteurId) {
            toast.error('Sélectionnez un apporteur');
            return;
        }

        setConfirming(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/reassign-apporteur`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}`
                    },
                    body: JSON.stringify({
                        new_apporteur_id: selectedApporteurId,
                        confirm: false  // Demander confirmation d'abord
                    })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                // Erreur (facture existante, etc.)
                if (data.detail?.error === 'already_invoiced') {
                    toast.error(data.detail.message, { duration: 8000 });
                } else if (data.detail?.error === 'commission_locked') {
                    toast.error(data.detail.message, { duration: 8000 });
                } else {
                    toast.error(data.detail?.message || data.detail || 'Erreur lors de la vérification');
                }
                return;
            }

            if (data.requires_confirmation) {
                // Afficher la confirmation
                setConfirmationData(data);
            }
        } catch (err) {
            console.error('Erreur:', err);
            toast.error('Erreur lors de la vérification');
        } finally {
            setConfirming(false);
        }
    };

    // Deuxième étape: confirmer la réattribution
    const handleConfirmReassign = async () => {
        setConfirming(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/reassign-apporteur`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}`
                    },
                    body: JSON.stringify({
                        new_apporteur_id: selectedApporteurId,
                        confirm: true  // Confirmer cette fois
                    })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                toast.error(data.detail?.message || data.detail || 'Erreur lors de la réattribution');
                return;
            }

            toast.success('Dossier réattribué avec succès');
            setOpen(false);
            if (onReassigned) onReassigned();
        } catch (err) {
            console.error('Erreur:', err);
            toast.error('Erreur lors de la réattribution');
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button 
                    variant="outline" 
                    size="sm"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                    data-testid="reassign-apporteur-btn"
                >
                    <Users className="w-4 h-4 mr-1" />
                    Réattribuer
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        Réattribuer le dossier
                    </DialogTitle>
                    <DialogDescription>
                        Transférer ce dossier à un apporteur de vos équipes ou à un VP payeur que vous supervisez
                    </DialogDescription>
                </DialogHeader>

                {/* Apporteur actuel */}
                {prospect.dossier_apporteur_name && (
                    <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                        <UserMinus className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-600">Apporteur actuel:</span>
                        <Badge variant="secondary">{prospect.dossier_apporteur_name}</Badge>
                    </div>
                )}

                {/* Sélection nouvel apporteur */}
                {!confirmationData && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                                <UserPlus className="w-4 h-4 text-purple-500" />
                                Nouvel apporteur
                            </label>
                            {loading ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                </div>
                            ) : teamApporteurs.length === 0 ? (
                                <div className="p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                                    Aucun apporteur ou VP payeur disponible dans vos équipes
                                </div>
                            ) : (
                                <Select value={selectedApporteurId} onValueChange={setSelectedApporteurId}>
                                    <SelectTrigger data-testid="select-new-apporteur">
                                        <SelectValue placeholder="Choisir un membre..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {teamApporteurs.map((apporteur) => (
                                            <SelectItem key={apporteur.id} value={apporteur.id}>
                                                <span className="flex items-center gap-2">
                                                    {apporteur.first_name} {apporteur.last_name}
                                                    {apporteur.type === 'vp_payeur' && (
                                                        <Badge variant="secondary" className="text-[10px] bg-purple-100 text-purple-700">VP Payeur</Badge>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>
                )}

                {/* Confirmation */}
                {confirmationData && (
                    <div className="space-y-4">
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                <div className="space-y-2">
                                    <p className="font-medium text-amber-800">
                                        {confirmationData.message}
                                    </p>
                                    <div className="text-sm text-amber-700 space-y-1">
                                        <p>
                                            <span className="text-slate-500">De:</span>{' '}
                                            <strong>{confirmationData.details?.old_apporteur}</strong>
                                        </p>
                                        <p>
                                            <span className="text-slate-500">Vers:</span>{' '}
                                            <strong>{confirmationData.details?.new_apporteur}</strong>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setOpen(false)}
                        disabled={confirming}
                    >
                        Annuler
                    </Button>
                    
                    {!confirmationData ? (
                        <Button
                            onClick={handleRequestConfirmation}
                            disabled={confirming || !selectedApporteurId || teamApporteurs.length === 0}
                            data-testid="request-confirmation-btn"
                        >
                            {confirming ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : null}
                            Vérifier
                        </Button>
                    ) : (
                        <Button
                            onClick={handleConfirmReassign}
                            disabled={confirming}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="confirm-reassign-btn"
                        >
                            {confirming ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : null}
                            Oui, réattribuer
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ReassignApporteur;
